import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { FaArrowLeft } from "react-icons/fa";
import Handler from "../../Helpers/Handler";
import { currencyFormatter, formatDate, formatDateTime, textCapitalize } from "../../Helpers/Utils";
import { DetailCard, FieldRow, Skeleton } from "../../Components/Needed";
import StaffRoleModal from "./StaffRoleModal";
import StaffPasswordModal from "./StaffPasswordModal";
import Timeline from "../../Components/Timeline";
import { listBackPath } from "../../Helpers/useListParams";

const formatPreviousValue = (value) => {
    if (value === undefined) return null;
    if (value === null || value === "") return "empty";
    if (Array.isArray(value)) return value.length ? value.join(", ") : "empty";
    if (typeof value === "object") return JSON.stringify(value);
    return String(value);
};

const formatAddress = (address) => {
    if (!address) return null;
    const parts = [address.street, address.city, address.state, address.postalCode, address.country].filter(Boolean);
    return parts.length ? parts.join(", ") : null;
};

const EMPLOYMENT_STATUS_BADGE = {
    active: "text-bg-success",
    suspended: "text-bg-warning",
    terminated: "text-bg-danger",
};

const StaffDetail = () => {
    const { staffId } = useParams();
    const navigate = useNavigate();
    const [staff, setStaff] = useState(null);
    const [logs, setLogs] = useState([]);
    const [departments, setDepartments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [statusUpdating, setStatusUpdating] = useState(false);
    const [showRoleModal, setShowRoleModal] = useState(false);
    const [showPasswordModal, setShowPasswordModal] = useState(false);
    const [roleDepartmentId, setRoleDepartmentId] = useState("");

    const load = useCallback(async () => {
        setLoading(true);
        const [staffResponse, logResponse, departmentsResponse] = await Promise.all([
            Handler({ method: "GET", url: `/staff/staff/${staffId}` }),
            Handler({ method: "GET", url: `/staff/staff/${staffId}/log`, params: { limit: 20 } }),
            Handler({ method: "GET", url: "/staff/staff/department/active" }),
        ]);
        const staffData = staffResponse?.success ? staffResponse.data.data : null;
        setStaff(staffData);
        setLogs(logResponse?.success ? logResponse?.data?.data || [] : []);
        setDepartments(departmentsResponse?.success ? departmentsResponse?.data?.data || [] : []);
        if (staffData?.roleId) {
            const roleResponse = await Handler({ method: "GET", url: `/staff/staff/role/${staffData.roleId}` });
            setRoleDepartmentId(roleResponse?.success ? roleResponse?.data?.data?.departmentId || "" : "");
        }
        setLoading(false);
    }, [staffId]);

    useEffect(() => { load(); }, [load]);

    const changeStatus = async (employmentStatus) => {
        if (employmentStatus === staff?.employmentStatus) return;
        setStatusUpdating(true);
        const response = await Handler({ method: "patch", url: `/staff/staff/${staffId}/status`, data: { employmentStatus } });
        if (response?.success) {
            toast.success(response?.data?.message || "Status updated");
            load();
        } else {
            toast.error(textCapitalize(response?.data?.message || "Unable to update status"));
        }
        setStatusUpdating(false);
    };

    if (loading) {
        return (
            <div className="settings-card">
                <Skeleton height={24} width="30%" className="mb-3" />
                <Skeleton height={220} />
            </div>
        );
    }

    if (!staff) {
        return (
            <div className="settings-card text-center py-5">
                <p className="text-muted mb-3">Staff member not found.</p>
                <button type="button" className="btn back-btn" onClick={() => navigate(listBackPath("/staff"))}>Back to staff</button>
            </div>
        );
    }

    const isTerminated = staff.employmentStatus === "terminated";
    const hasFinancialInfo = staff.baseSalary !== undefined || staff.allowances !== undefined || staff.bankDetails || staff.uanNumber || staff.pfNumber || staff.esiNumber;
    const hasEmergencyContact = staff.emergencyContact && Object.values(staff.emergencyContact).some(Boolean);

    return (
        <>
            <button type="button" className="btn back-btn text-decoration-none px-3 mb-3 d-inline-flex align-items-center gap-2" onClick={() => navigate(listBackPath("/staff"))}>
                <FaArrowLeft /> Back to staff
            </button>

            <div className="row g-4">
                <div className="col-12 col-lg-7">
                    <DetailCard title={`${staff.employeeId} - ${staff.name}`}>
                        <div className="d-flex align-items-center gap-2 mb-3 flex-wrap">
                            <span className={`badge rounded-pill text-capitalize ${EMPLOYMENT_STATUS_BADGE[staff.employmentStatus] || "text-bg-secondary"}`}>{staff.employmentStatus}</span>

                            {isTerminated ? (
                                <span className="text-muted small">Terminated staff can't be reactivated here.</span>
                            ) : (
                                <div className="dropdown">
                                    <button type="button" className="btn btn-sm btn-outline-secondary dropdown-toggle" data-bs-toggle="dropdown" disabled={statusUpdating}>
                                        {statusUpdating ? "Updating..." : "Change status"}
                                    </button>
                                    <ul className="dropdown-menu">
                                        <li><button type="button" className="dropdown-item" onClick={() => changeStatus("active")}>Active</button></li>
                                        <li><button type="button" className="dropdown-item" onClick={() => changeStatus("suspended")}>Suspended</button></li>
                                        <li><button type="button" className="dropdown-item text-danger" onClick={() => changeStatus("terminated")}>Terminated</button></li>
                                    </ul>
                                </div>
                            )}

                            <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => setShowRoleModal(true)}>Reassign role</button>
                            <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => setShowPasswordModal(true)}>Reset password</button>
                            <button type="button" className="btn btn-sm btn-primary ms-auto" onClick={() => navigate(`/staff/${staffId}/edit`)}>Edit</button>
                        </div>

                        <FieldRow label="Designation" value={staff.designation} />
                        <FieldRow label="Role" value={staff.roleName} />
                        <FieldRow label="Department" value={staff.departmentName} />
                        <FieldRow label="Hub" value={staff.hubId} />
                        <FieldRow label="Employment type" value={textCapitalize(staff.employmentType)} />
                        <FieldRow label="Date of joining" value={formatDate(staff.dateOfJoining)} />
                        <FieldRow label="Probation end" value={staff.probationEndDate ? formatDate(staff.probationEndDate) : null} />
                        <FieldRow label="Reports to" value={staff.reportingTo} />
                        <FieldRow label="Phone" value={staff.phone} />
                        <FieldRow label="Alternate phone" value={staff.alternatePhone} />
                        <FieldRow label="Email" value={staff.email} />
                        <FieldRow label="Date of birth" value={staff.dateOfBirth ? formatDate(staff.dateOfBirth) : null} />
                        <FieldRow label="Gender" value={textCapitalize(staff.gender)} />
                        <FieldRow label="Blood group" value={staff.bloodGroup} />
                        <FieldRow label="Marital status" value={staff.maritalStatus ? textCapitalize(staff.maritalStatus) : null} />
                        <FieldRow label="Current address" value={formatAddress(staff.currentAddress)} />
                        <FieldRow label="Permanent address" value={formatAddress(staff.permanentAddress)} />
                        <FieldRow label="Aadhaar number" value={staff.aadhaarNumber} />
                        <FieldRow label="PAN number" value={staff.panNumber} />
                        <FieldRow label="Driving licence" value={staff.drivingLicenseNumber} />
                        <FieldRow label="DL expiry" value={staff.drivingLicenseExpiry ? formatDate(staff.drivingLicenseExpiry) : null} />
                        <FieldRow label="Highest education" value={staff.highestEducation} />
                        <FieldRow label="Certifications" value={staff.certifications?.length ? staff.certifications.join(", ") : null} />
                        <FieldRow label="Languages known" value={staff.languagesKnown?.length ? staff.languagesKnown.join(", ") : null} />
                        <FieldRow label="Created" value={formatDateTime(staff.createdAt)} />
                        <FieldRow label="Last updated" value={formatDateTime(staff.updatedAt)} />
                    </DetailCard>

                    {hasFinancialInfo ? (
                        <DetailCard title="Financial">
                            <FieldRow label="Base salary" value={staff.baseSalary !== undefined ? currencyFormatter(staff.baseSalary) : null} />
                            <FieldRow label="Allowances" value={staff.allowances !== undefined ? currencyFormatter(staff.allowances) : null} />
                            <FieldRow label="Bank account" value={staff.bankDetails?.accountNumber} />
                            <FieldRow label="Bank name" value={staff.bankDetails?.bankName} />
                            <FieldRow label="Account holder" value={staff.bankDetails?.bankHolder} />
                            <FieldRow label="IFSC" value={staff.bankDetails?.ifsc} />
                            <FieldRow label="UAN number" value={staff.uanNumber} />
                            <FieldRow label="PF number" value={staff.pfNumber} />
                            <FieldRow label="ESI number" value={staff.esiNumber} />
                        </DetailCard>
                    ) : ""}

                    {hasEmergencyContact ? (
                        <DetailCard title="Emergency Contact">
                            <FieldRow label="Name" value={staff.emergencyContact.name} />
                            <FieldRow label="Relationship" value={staff.emergencyContact.relationship} />
                            <FieldRow label="Phone" value={staff.emergencyContact.phone} />
                            <FieldRow label="Email" value={staff.emergencyContact.email} />
                        </DetailCard>
                    ) : ""}
                </div>

                <div className="col-12 col-lg-5">
                    {/* <DetailCard title="Activity Log">
                        {logs.length ? (
                            <ul className="hub-log-list">
                                {logs.map((log) => (
                                    <li key={log._id || `${log.createdAt}-${log.action}`} className="hub-log-item">
                                        <div className="d-flex align-items-center justify-content-between gap-2">
                                            <span className={`badge rounded-pill ${log.action === "Write" ? "text-bg-primary" : "text-bg-secondary"}`}>{log.action}</span>
                                            <span className="text-muted small">{formatDateTime(log.createdAt)}</span>
                                        </div>
                                        <div className="small mt-1">{log?.previousData?.msg || "-"}</div>
                                        {log?.previousData?.data && Object.keys(log.previousData.data).length ? (
                                            <div className="hub-log-diff">
                                                {Object.entries(log.previousData.data).map(([field, value]) => (
                                                    <div key={field}>
                                                        <span className="text-muted">{field}:</span> was {formatPreviousValue(value)}
                                                    </div>
                                                ))}
                                            </div>
                                        ) : ""}
                                        <div className="text-muted small mt-1">By {log.userId}</div>
                                    </li>
                                ))}
                            </ul>
                        ) : (
                            <p className="text-muted mb-0">No activity recorded yet.</p>
                        )}
                    </DetailCard> */}
                    <Timeline userLog={logs} />
                </div>
            </div>

            {showRoleModal ? (
                <StaffRoleModal
                    staffId={staffId}
                    currentDepartmentId={roleDepartmentId}
                    currentRoleId={staff.roleId}
                    departments={departments}
                    onClose={() => setShowRoleModal(false)}
                    onSaved={() => { setShowRoleModal(false); load(); }}
                />
            ) : ""}

            {showPasswordModal ? (
                <StaffPasswordModal
                    staffId={staffId}
                    onClose={() => setShowPasswordModal(false)}
                    onSaved={() => setShowPasswordModal(false)}
                />
            ) : ""}
        </>
    );
};

export default StaffDetail;
